import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { INewHireExceptionHistoric } from 'src/interfaces';
import { DefaultTable } from 'src/components';
import { useMutation, useQuery } from '@tanstack/react-query';
import NewHireExceptionService from 'src/services/new-hire-exception.service';
import { useTypedSelector } from 'src/hooks/useTypedSelector';
import { AlertAdapter, DataTableCellDeleteButton } from 'pg-ui-library';
import {
  NewHireExceptionHistoricContainer,
  NewHireExceptionHistoricHeader,
  NewHireExceptionHistoricTitle,
} from './styles';

const customStyles = {
  headRow: {
    style: {
      border: 'none',
    },
  },
  headCells: {
    style: {
      color: '#9399B2',
      font: 'normal normal bold 16px Biotif',
      '&:nth-of-type(5)': {
        marginLeft: '25%',
      },
    },
  },
  rows: {
    style: {
      font: 'normal normal normal 14px Biotif',
      color: '#1B1D35',
      borderRadius: '5px',
      '&:not(:last-of-type)': {
        borderBottomWidth: '0px',
      },
    },
    stripedStyle: {
      color: '#1B1D35',
      backgroundColor: '#F3F4F8',
    },
    highlightOnHoverStyle: {
      backgroundColor: 'rgb(230, 244, 244)',
      borderBottomColor: '#FFFFFF',
      borderRadius: '25px',
      outline: '1px solid #FFFFFF',
    },
  },
  cells: {
    style: {
      marginLeft: '2px',
      '&:nth-of-type(5)': {
        justifyContent: 'center',
        marginLeft: '25%',
      },
    },
  },
  pagination: {
    style: {
      border: 'none',
    },
  },
};

const NewHireExceptionHistoric: React.FC = () => {
  const [totalRows, setTotalRows] = useState(0);
  const [historicItems, setHistoricItems] = useState();
  const { selectedEmployee } = useTypedSelector(state => state.employees.data);
  const { refetch: refetchHistoric, data: historic } = useQuery(['employee/new-hire-historic'], () =>
    NewHireExceptionService.getHirtoric(selectedEmployee.employee?.id as unknown as '')
  );
  const { t } = useTranslation();

  useEffect(() => {
    if (historic?.data) {
      setHistoricItems(historic?.data.items);
      setTotalRows(historic?.data.totalItems || 0);
    }
  }, [historic]);

  const mutation = useMutation((exceptionId: string) => NewHireExceptionService.deleteHirtoric(exceptionId), {
    onSuccess: () => {
      AlertAdapter('Historic deleted successfully', 'success');
      refetchHistoric();
      setHistoricItems(historic?.data.items);
    },
    onError: () => {
      AlertAdapter('Failed to delete', 'error');
    },
  });

  const deleteExceptionButton = (onClick: Function) =>
    !mutation.isLoading && <DataTableCellDeleteButton onClick={() => onClick()} />;

  const deleteException = (exceptionId: string) => {
    mutation.mutate(exceptionId);
  };

  const columns = useMemo(
    () => [
      {
        name: 'Employee Name',
        selector: (row: INewHireExceptionHistoric) => row.employeeId,
        width: '244px',
      },
      {
        name: 'Advanced by',
        selector: (row: INewHireExceptionHistoric) => row.userId,
        width: '244px',
      },
      {
        name: 'Advanced to',
        selector: (row: INewHireExceptionHistoric) => row?.advance,
        sortable: true,
        width: '174px',
      },
      {
        name: 'Extend To',
        selector: (row: INewHireExceptionHistoric) => row?.extends,
        sortable: true,
        width: '184px',
      },
      {
        name: 'Cancel Event',
        width: '130px',
        cell: (row: any) => deleteExceptionButton(() => deleteException(row.id)),
      },
    ],
    []
  );

  return (
    <NewHireExceptionHistoricContainer>
      <NewHireExceptionHistoricHeader>
        <NewHireExceptionHistoricTitle>{t('newhireException.modal.changes')}</NewHireExceptionHistoricTitle>
      </NewHireExceptionHistoricHeader>
      <DefaultTable
        customStyles={customStyles}
        columns={columns}
        data={historicItems || []}
        paginationTotalRows={totalRows}
        pagination
      />
    </NewHireExceptionHistoricContainer>
  );
};
export default NewHireExceptionHistoric;
